import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { rgbaToCss } from '../../core/color';
import { getActiveDocument, useAppStore } from '../../state/store';
import {
  traceActiveLayer,
  vectorizeActiveLayer,
  type VectorizeSettings,
} from '../../state/vectorActions';
import { pathToPolylines } from '../../vector/geometry';
import type { TracedRegion } from '../../vector/vectorize';
import { Checkbox, Modal, SliderRow } from '../components/controls';

const PREVIEW_MAX = 320;

const DEFAULTS: VectorizeSettings = {
  colors: 8,
  threshold: 127,
  tolerance: 1.5,
  smooth: true,
  minArea: 12,
};

/** Draws the traced regions the same way the shape layers will be rasterised. */
function drawRegions(
  context: CanvasRenderingContext2D,
  regions: readonly TracedRegion[],
  width: number,
  height: number,
): void {
  context.clearRect(0, 0, width, height);
  for (const region of regions) {
    const polylines = pathToPolylines(region.subpaths);
    context.fillStyle = rgbaToCss(region.color);
    context.beginPath();
    polylines.forEach((polyline) => {
      if (polyline.length < 3) return;
      context.moveTo(polyline[0].x, polyline[0].y);
      for (let i = 1; i < polyline.length; i += 1) context.lineTo(polyline[i].x, polyline[i].y);
      context.closePath();
    });
    context.fill('evenodd');
  }
}

export function VectorizeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const doc = useAppStore(getActiveDocument);
  const [settings, setSettings] = useState<VectorizeSettings>(DEFAULTS);
  const [preview, setPreview] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The dialog body mounts in a portal one commit after this component renders,
  // so the effect has to wait for the canvas to report itself as attached.
  const [attached, setAttached] = useState(false);
  const attach = useCallback((node: HTMLCanvasElement | null) => {
    canvasRef.current = node;
    setAttached(node !== null);
  }, []);

  const patch = (part: Partial<VectorizeSettings>) =>
    setSettings((current) => ({ ...current, ...part }));

  const regions = useMemo(
    () => (open && preview ? traceActiveLayer(settings) : []),
    [open, preview, settings],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!attached || !canvas || !context || !doc) return;
    canvas.width = doc.width;
    canvas.height = doc.height;
    drawRegions(context, regions, doc.width, doc.height);
  }, [attached, regions, doc]);

  const scale = doc ? Math.min(1, PREVIEW_MAX / Math.max(doc.width, doc.height)) : 1;
  const shapeCount = regions.reduce((total, region) => total + region.subpaths.length, 0);

  const apply = () => {
    vectorizeActiveLayer(settings);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Vectorize Bitmap"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button button--primary" onClick={apply}>
            Vectorize
          </button>
        </>
      }
    >
      <p className="dialog__note">
        Traces the active layer into a group of shape layers, one per colour. Fewer colours and a
        higher tolerance give simpler paths.
      </p>

      <canvas
        ref={attach}
        className="colorrange__preview"
        style={{
          width: doc ? doc.width * scale : undefined,
          height: doc ? doc.height * scale : undefined,
        }}
      />

      <SliderRow
        label="Colors"
        value={settings.colors}
        min={2}
        max={32}
        onChange={(colors) => patch({ colors })}
      />
      <SliderRow
        label="Tolerance"
        value={settings.tolerance}
        min={0}
        max={10}
        step={0.5}
        onChange={(tolerance) => patch({ tolerance })}
      />
      <SliderRow
        label="Min area"
        value={settings.minArea}
        min={1}
        max={500}
        onChange={(minArea) => patch({ minArea })}
      />
      <SliderRow
        label="Alpha cutoff"
        value={settings.threshold}
        min={0}
        max={254}
        onChange={(threshold) => patch({ threshold })}
      />

      <div className="field">
        <Checkbox
          label="Smooth curves"
          checked={settings.smooth}
          onChange={(smooth) => patch({ smooth })}
        />
        <Checkbox label="Preview" checked={preview} onChange={setPreview} />
      </div>

      <p className="dialog__note">
        {preview ? `${regions.length} colours, ${shapeCount} subpaths` : 'Preview is off'}
      </p>
    </Modal>
  );
}
